import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AbstractControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AuthService } from '../shared/services/auth.service';
import { TreeService } from '../shared/services/tree.service';
import { TreeInvitationInfo } from '../shared/models/tree.model';
import { Globals } from '../shared/globals';
import { TranslatePipe } from '../shared/i18n/translate.pipe';

/** Hasła w obu polach muszą być takie same */
const passwordsMatch = (group: AbstractControl): ValidationErrors | null =>
  group.get('password')?.value === group.get('passwordRepeat')?.value ? null : { passwordMismatch: true };

/**
 * Strona przyjęcia zaproszenia do drzewa (link z maila: /invitation/:token).
 * Token jest sekretem, więc dane zaproszenia pobieramy bez logowania - dopiero
 * samo przyjęcie wymaga konta. Osoba bez konta może je założyć na miejscu;
 * adres e-mail pochodzi wtedy z zaproszenia, więc konto jest od razu potwierdzone.
 */
@Component({
    selector: 'app-invitation',
    templateUrl: './invitation.component.html',
    styleUrls: ['./invitation.component.css'],
    standalone: true,
    imports: [ReactiveFormsModule, MatButtonModule, MatFormFieldModule, MatInputModule, RouterLink, TranslatePipe]
})
export class InvitationComponent implements OnInit {

  token = '';
  invitation: TreeInvitationInfo | undefined;
  loading = true;
  /** Zaproszenie nie istnieje albo nie da się go już wykorzystać */
  invalid = false;
  accepting = false;
  /** Widoczny formularz zakładania konta */
  showSignup = false;
  facebookAvailable = false;

  usernameFormControl = new UntypedFormControl('', [Validators.required]);
  nameFormControl = new UntypedFormControl('', [Validators.required]);
  surnameFormControl = new UntypedFormControl('', [Validators.required]);
  passwordFormControl = new UntypedFormControl('', [Validators.required, Validators.minLength(6)]);
  passwordRepeatFormControl = new UntypedFormControl('', [Validators.required]);

  signupForm = new UntypedFormGroup({
    username: this.usernameFormControl,
    name: this.nameFormControl,
    surname: this.surnameFormControl,
    password: this.passwordFormControl,
    passwordRepeat: this.passwordRepeatFormControl
  }, { validators: passwordsMatch });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private treeService: TreeService,
    public authService: AuthService
  ) { }

  ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token') ?? '';
    if (!this.token) {
      this.loading = false;
      this.invalid = true;
      return;
    }

    this.treeService.getInvitationByToken(this.token).subscribe({
      next: (invitation) => {
        this.invitation = invitation;
        this.invalid = !invitation || !!invitation.expired || !!invitation.accepted;
        this.loading = false;
        // login podpowiadamy z części adresu przed małpą
        const local = invitation?.email?.split('@')[0] ?? '';
        this.usernameFormControl.setValue(local);
      },
      error: () => {
        this.invalid = true;
        this.loading = false;
      }
    });

    this.authService.getAuthProviders().subscribe({
      next: (providers) => this.facebookAvailable = !!providers?.facebook,
      error: () => this.facebookAvailable = false
    });
  }

  membershipLevelName() {
    return this.invitation?.membershiplevel ? Globals.getMembershipLevel(this.invitation.membershiplevel) : '';
  }

  onSignin() {
    // Po zalogowaniu wracamy na ten sam link, żeby dokończyć przyjęcie zaproszenia
    this.authService.setRedirectUrl(this.router.url);
    this.router.navigate(['/signin']);
  }

  onFacebook() {
    this.authService.signinFacebook(this.router.url);
  }

  onShowSignup() {
    this.showSignup = true;
  }

  onSignup() {
    if (this.signupForm.invalid || this.accepting || this.invalid) {
      return;
    }
    this.accepting = true;

    this.treeService.signupFromInvitation(this.token, {
      username: (this.usernameFormControl.value as string).trim(),
      legal_name: this.nameFormControl.value,
      legal_surname: this.surnameFormControl.value,
      password: this.passwordFormControl.value
    }).subscribe({
      next: (result) => {
        this.accepting = false;
        // Błąd biznesowy wraca jako sama koperta ze statusem (bez danych)
        if (!result?.tree_url) {
          return;
        }
        // Backend założył konto i od razu otworzył sesję
        localStorage.setItem('username', result.username);
        localStorage.setItem('userid', result.userId);
        localStorage.setItem('expires', String(result.expires));
        this.authService.authStateChanged.next(true);
        this.router.navigate(['/tree', result.tree_url]);
      },
      error: () => this.accepting = false
    });
  }

  onAccept() {
    if (!this.invitation || this.invalid || this.accepting) {
      return;
    }
    this.accepting = true;
    this.treeService.acceptInvitation(this.token).subscribe({
      next: (result) => {
        this.accepting = false;
        // Błąd biznesowy wraca jako koperta ze statusem (bez danych) - wtedy nie przechodzimy dalej
        if (!result?.tree_url) {
          this.invalid = true;
          return;
        }
        this.router.navigate(['/tree', result.tree_url]);
      },
      error: () => {
        this.accepting = false;
        this.invalid = true;
      }
    });
  }

}
